import React, { useState, useEffect } from "react";
import { formatUTC } from "../utils/date";

import ApiClient from "../services/fuelandmoreapi";

import type { TBunkerPrice, TFuelGrade } from "@/types";

import SearchPort from "./SearchPort";
import BinIcon from "./BinIcon";

import { jwtDecode } from "jwt-decode";

import { config } from "../config";

import "./BunkerPrices.css";
// Reuse pagination styling from RequestForFirmList
import "./RequestForFirmList.css";

interface DecodedToken {
  email?: string;
  [key: string]: any;
}

const BunkerPrices: React.FC = () => {
  const [bunkerId, setBunkerId] = useState<string>("");
  const [bunkerPrices, setBunkerPrices] = useState<TBunkerPrice[]>([]);
  const [accountManagerPrices, setAccountManagerPrices] = useState<
    TBunkerPrice[]
  >([]);
  // Server-side paging state for "Your Contributed Prices"
  const [amPage, setAmPage] = useState(1);
  const amPageSize = 10;
  const [amTotal, setAmTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TBunkerPrice | null>(null);

  // Form state
  const [fuelGrade, setFuelGrade] = useState<TFuelGrade | undefined>(undefined);
  const [price, setPrice] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const { apiBaseUrl } = config;

  // Get user email from JWT
  let userEmail = "";
  const idToken = localStorage.getItem("id_token");
  if (idToken) {
    try {
      const decoded: DecodedToken = jwtDecode(idToken);
      userEmail = decoded.email || "";
    } catch {
      userEmail = "";
    }
  }

  useEffect(() => {
    if (userEmail) {
      fetchAccountManagerPrices(userEmail);
    }
  }, [userEmail, amPage]);

  const handlePortSelect = (port: { unlocode: string }) => {
    setBunkerId(port.unlocode);
    fetchBunkerPrices(port.unlocode);
  };

  const fetchBunkerPrices = async (unlocode: string) => {
    setLoading(true);
    setError(null);
    try {
      const api = new ApiClient(apiBaseUrl);
      const response = await api.getBunkerPrices(unlocode);
      // Support both .data and direct array
      const prices: TBunkerPrice[] = response.data ?? response;
      // Sort by fuelGrade, then timestamp (descending)
      prices.sort((a, b) => {
        if (a.fuelGrade < b.fuelGrade) return -1;
        if (a.fuelGrade > b.fuelGrade) return 1;
        return (
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
      });
      setBunkerPrices(prices);
    } catch (err) {
      setError("Failed to fetch bunker prices.");
      setBunkerPrices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountManagerPrices = async (email: string) => {
    setLoading(true);
    setError(null);
    try {
      const api = new ApiClient(apiBaseUrl);
      const response = await api.getBunkerPricesForAccountManager(email, {
        page: amPage,
        pageSize: amPageSize,
      });
      const payload = response.data ?? response;
      const prices: TBunkerPrice[] = Array.isArray(payload)
        ? payload
        : (payload.items ?? []);
      setAccountManagerPrices(prices);
      if (!Array.isArray(payload) && typeof payload.total === "number") {
        setAmTotal(payload.total);
        const totalPages = Math.max(1, Math.ceil(payload.total / amPageSize));
        if (amPage > totalPages) setAmPage(totalPages);
      } else {
        setAmTotal(prices.length);
      }
    } catch (err) {
      setError("Failed to fetch account manager bunker prices.");
      setAccountManagerPrices([]);
      setAmTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBunkerPrice = (bp: TBunkerPrice) => {
    setConfirmDelete(bp);
  };

  const confirmDeleteBunkerPrice = async () => {
    if (!confirmDelete) return;
    try {
      const api = new ApiClient(apiBaseUrl);
      if (confirmDelete.id) {
        await api.deleteBunkerPrice(confirmDelete.id);
      } else {
        // Fallback: construct expected id from bunkerId + timestamp iso if missing
        const constructedId = `${confirmDelete.bunkerId}_${new Date(confirmDelete.timestamp).toISOString()}`;
        await api.deleteBunkerPrice(constructedId);
      }
      setConfirmDelete(null);
      if (bunkerId) fetchBunkerPrices(bunkerId);
      if (userEmail) fetchAccountManagerPrices(userEmail);
    } catch (err) {
      setError("Failed to delete bunker price.");
      setConfirmDelete(null);
    }
  };

  const cancelDeleteBunkerPrice = () => setConfirmDelete(null);

  const handleAddBunkerPrice = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const missing: string[] = [];
    if (!bunkerId) missing.push("port");
    if (!fuelGrade) missing.push("fuel grade");
    if (!price) missing.push("price");
    if (missing.length) {
      setFormError(
        `Please provide: ${missing
          .join(", ")
          .replace(/, ([^,]*)$/, " and $1")}.`
      );
      return;
    }
    try {
      const api = new ApiClient(apiBaseUrl);
      const now = new Date();
      const id = `${bunkerId}_${now.toISOString()}`;
      const newBunkerPrice: TBunkerPrice = {
        id,
        bunkerId,
        fuelGrade: fuelGrade as string,
        price: parseFloat(price),
        source: userEmail,
        timestamp: now,
      };
      await api.createBunkerPrice(newBunkerPrice);
      // Keep fuel grade selected for quicker repeated entry; just clear price.
      setPrice("");
      fetchBunkerPrices(bunkerId);
      fetchAccountManagerPrices(userEmail); // Refresh account manager prices
    } catch (err) {
      setFormError("Failed to add bunker price.");
    }
  };

  return (
    <div className="bunker-prices-container">
      <h3>Bunker Prices</h3>
      <SearchPort onSelect={handlePortSelect} />
      {bunkerId && (
        <>
          <br />
          <h3>Prices for Port: {bunkerId}</h3>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div style={{ color: "red" }}>{error}</div>
          ) : (
            <table className="bunker-prices-table">
              <thead>
                <tr>
                  <th>Fuel Grade</th>
                  <th>Price</th>
                  <th>Source</th>
                  <th>Timestamp (UTC)</th>
                </tr>
              </thead>
              <tbody>
                {bunkerPrices.map((bp, idx) => (
                  <tr key={idx}>
                    <td>{bp.fuelGrade}</td>
                    <td>${bp.price.toFixed(2)}</td>
                    <td>{bp.source}</td>
                    <td>{formatUTC(bp.timestamp, "datetime")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <br />
          <h3>Add New Bunker Price</h3>
          <form onSubmit={handleAddBunkerPrice} className="bunker-price-form">
            <div className="form-group">
              <label htmlFor="fuelGrade">Fuel Grade:</label>
              <select
                id="fuelGrade"
                value={fuelGrade}
                onChange={(e) => setFuelGrade(e.target.value as TFuelGrade)}
                required
              >
                <option value="">Select Fuel Grade</option>
                <option value="Biofuel">Biofuel</option>
                <option value="hsfo">HSFO</option>
                <option value="mgo">MGO</option>
                <option value="mgo_biofuel">MGO Biofuel</option>
                <option value="ulsfo">ULSFO</option>
                <option value="vlsfo">VLSFO</option>
                <option value="vlsfo_biofuel">VLSFO Biofuel</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="price">Price:</label>
              <input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit">Add Bunker Price</button>
            </div>
            {formError && <div style={{ color: "red" }}>{formError}</div>}
          </form>
        </>
      )}
      {userEmail && (
        <>
          <br></br>
          <h3>Your Contributed Bunker Prices</h3>
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div style={{ color: "red" }}>{error}</div>
          ) : (
            <>
              {/* Top pagination */}
              <div className="request-for-firm-pagination-bar">
                <a
                  href="#prev"
                  className={`pager-link ${amPage === 1 ? "disabled" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (amPage > 1) setAmPage(amPage - 1);
                  }}
                  aria-disabled={amPage === 1}
                >
                  Prev
                </a>
                <span className="pager-status">
                  Page {amPage} of{" "}
                  {Math.max(
                    1,
                    Math.ceil(
                      ((amTotal ?? accountManagerPrices.length) || 0) /
                        amPageSize
                    )
                  )}
                </span>
                <a
                  href="#next"
                  className={`pager-link ${
                    amPage ===
                    Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    )
                      ? "disabled"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    const totalPages = Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    );
                    if (amPage < totalPages) setAmPage(amPage + 1);
                  }}
                  aria-disabled={
                    amPage ===
                    Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    )
                  }
                >
                  Next
                </a>
              </div>

              <table className="bunker-prices-table">
                <thead>
                  <tr>
                    <th>Fuel Grade</th>
                    <th>Price</th>
                    <th>Port</th>
                    <th>Timestamp (UTC)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {accountManagerPrices.map((bp, idx) => (
                    <tr key={idx}>
                      <td>{bp.fuelGrade}</td>
                      <td>${bp.price.toFixed(2)}</td>
                      <td>{bp.bunkerId}</td>
                      <td>{formatUTC(bp.timestamp, "datetime")}</td>
                      <td>
                        <button
                          title="Delete"
                          aria-label={`Delete price for ${bp.bunkerId}`}
                          className="icon-button"
                          onClick={() => handleDeleteBunkerPrice(bp)}
                          type="button"
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          <BinIcon size={18} color="black" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Bottom pagination */}
              <div className="request-for-firm-pagination-bar bottom">
                <a
                  href="#prev"
                  className={`pager-link ${amPage === 1 ? "disabled" : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (amPage > 1) setAmPage(amPage - 1);
                  }}
                  aria-disabled={amPage === 1}
                >
                  Prev
                </a>
                <span className="pager-status">
                  Page {amPage} of{" "}
                  {Math.max(
                    1,
                    Math.ceil(
                      ((amTotal ?? accountManagerPrices.length) || 0) /
                        amPageSize
                    )
                  )}
                </span>
                <a
                  href="#next"
                  className={`pager-link ${
                    amPage ===
                    Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    )
                      ? "disabled"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    const totalPages = Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    );
                    if (amPage < totalPages) setAmPage(amPage + 1);
                  }}
                  aria-disabled={
                    amPage ===
                    Math.max(
                      1,
                      Math.ceil(
                        ((amTotal ?? accountManagerPrices.length) || 0) /
                          amPageSize
                      )
                    )
                  }
                >
                  Next
                </a>
              </div>
            </>
          )}
        </>
      )}
      {confirmDelete && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-message">
              Are you sure you want to delete price for port{" "}
              <b>{confirmDelete.bunkerId}</b>
              <br /> fuel grade <b>{confirmDelete.fuelGrade}</b>?
            </div>
            <div className="modal-actions">
              <button
                className="modal-btn confirm"
                onClick={confirmDeleteBunkerPrice}
              >
                Yes
              </button>
              <button
                className="modal-btn cancel"
                onClick={cancelDeleteBunkerPrice}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BunkerPrices;
